/**
 * Options for configuring the promise pool behavior.
 */
export interface PromisePoolOptions {
  /**
   * Maximum number of promises to execute concurrently.
   *
   * Controls how many tasks run in parallel at any given time.
   * A lower value reduces memory and CPU pressure; a higher value
   * increases throughput for I/O-bound workloads.
   *
   * @default 5
   */
  concurrency?: number;
}

/**
 * Discriminated union representing the outcome of a single task execution.
 *
 * - `{ success: true, value: T }` — the task resolved successfully.
 * - `{ success: false, error: unknown }` — the task rejected with an error.
 *
 * Using a discriminated union allows exhaustive type checking when
 * processing results.
 */
export type PromisePoolResult<T> = { success: true; value: T } | { success: false; error: unknown };

/**
 * Executes an array of async tasks with a configurable concurrency limit.
 *
 * ## Algorithm
 *
 * This function implements a worker-pool pattern:
 *
 * 1. A shared `nextIndex` counter tracks which task should be picked up next.
 * 2. `concurrency` number of worker coroutines are spawned.
 * 3. Each worker atomically claims the next available index (via the shared
 *    `nextIndex` counter) and executes the corresponding task.
 * 4. Results are stored in a pre-allocated array at the original index,
 *    preserving input order regardless of completion order.
 * 5. All tasks are executed even if some fail — errors are captured in the
 *    result objects rather than thrown.
 *
 * ## When to use
 *
 * Use `promisePool` when you want to process a batch of items with bounded
 * parallelism and need all results, including failures. This is ideal for
 * scenarios like batch API calls, file processing, or database operations
 * where partial failures are expected and should not abort the entire batch.
 *
 * @typeParam T - The type of items in the input array.
 * @typeParam R - The return type of each task function.
 *
 * @param items - Array of items to process.
 * @param taskFn - Async function to execute for each item. Receives the item
 *                 and its zero-based index.
 * @param options - Configuration options for the pool.
 *
 * @returns A promise that resolves to an array of results in the same order
 *          as the input. Each result is a discriminated union indicating
 *          success or failure.
 *
 * @throws {Error} If `concurrency` is less than 1.
 *
 * @example
 * ```ts
 * const results = await promisePool(
 *   [1, 2, 3, 4, 5],
 *   async (item) => {
 *     if (item === 3) throw new Error("fail");
 *     return item * 2;
 *   },
 *   { concurrency: 2 }
 * );
 * // results: [
 * //   { success: true, value: 2 },
 * //   { success: true, value: 4 },
 * //   { success: false, error: Error("fail") },
 * //   { success: true, value: 8 },
 * //   { success: true, value: 10 }
 * // ]
 * ```
 */
export async function promisePool<T, R>(
  items: T[],
  taskFn: (item: T, index: number) => Promise<R>,
  options: PromisePoolOptions = {}
): Promise<PromisePoolResult<R>[]> {
  // Resolve concurrency to a concrete value; default to 5.
  const concurrency = options.concurrency ?? 5;

  if (concurrency < 1) {
    throw new Error("Concurrency must be at least 1");
  }

  // Pre-allocate the results array to avoid reallocation and preserve order.
  // Sparse arrays are used because workers fill slots out of order.
  const results: PromisePoolResult<R>[] = new Array(items.length);

  // Shared index — each worker atomically claims the next task by reading
  // and incrementing this counter. Because JavaScript is single-threaded,
  // this read-modify-write sequence is naturally atomic.
  let nextIndex = 0;

  /**
   * Worker coroutine that repeatedly claims the next available task,
   * executes it, and stores the result (success or failure) at the
   * corresponding index.
   *
   * Workers exit when `nextIndex` reaches the end of the input array.
   */
  async function worker(): Promise<void> {
    while (nextIndex < items.length) {
      // Claim the next task index. This is safe because JS is single-threaded.
      const currentIndex = nextIndex;
      nextIndex += 1;

      try {
        const value = await taskFn(items[currentIndex], currentIndex);
        results[currentIndex] = { success: true, value };
      } catch (error) {
        // Capture the error instead of propagating it.
        results[currentIndex] = { success: false, error };
      }
    }
  }

  // Spawn up to `concurrency` workers, but never more than the number of items.
  // Spawning fewer workers than the concurrency limit would be wasteful when
  // there are fewer items than the concurrency setting.
  const workerCount = Math.min(concurrency, items.length);
  const workers = Array.from({ length: workerCount }, () => worker());

  // Wait for all workers to complete. Each worker runs until exhaustion.
  await Promise.all(workers);

  return results;
}

/**
 * Executes an array of async tasks with a configurable concurrency limit.
 * Fails fast: the first error causes the entire pool to reject.
 *
 * ## Algorithm
 *
 * Uses the same worker-pool pattern as `promisePool`, but without error
 * capturing. Any rejection propagates immediately through `Promise.all`,
 * which rejects as soon as any worker promise rejects.
 *
 * ## When to use
 *
 * Use `promisePoolStrict` when any failure should abort the entire operation.
 * This is appropriate for transactions, sequential workflows, or scenarios
 * where partial results are not useful.
 *
 * @typeParam T - The type of items in the input array.
 * @typeParam R - The return type of each task function.
 *
 * @param items - Array of items to process.
 * @param taskFn - Async function to execute for each item. Receives the item
 *                 and its zero-based index.
 * @param options - Configuration options for the pool.
 *
 * @returns A promise that resolves to an array of results in the same order
 *          as the input.
 *
 * @throws {Error} If any task rejects, or if `concurrency` is less than 1.
 *
 * @example
 * ```ts
 * const results = await promisePoolStrict(
 *   [1, 2, 3, 4, 5],
 *   async (item) => item * 2,
 *   { concurrency: 2 }
 * );
 * // results: [2, 4, 6, 8, 10]
 * ```
 */
export async function promisePoolStrict<T, R>(
  items: T[],
  taskFn: (item: T, index: number) => Promise<R>,
  options: PromisePoolOptions = {}
): Promise<R[]> {
  // Resolve concurrency to a concrete value; default to 5.
  const concurrency = options.concurrency ?? 5;

  if (concurrency < 1) {
    throw new Error("Concurrency must be at least 1");
  }

  // Pre-allocate the results array to preserve input order.
  const results: R[] = new Array(items.length);

  // Shared index — each worker atomically claims the next task.
  let nextIndex = 0;

  /**
   * Worker coroutine that repeatedly claims the next available task
   * and executes it. Any rejection propagates immediately.
   */
  async function worker(): Promise<void> {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;

      // Rejections propagate through Promise.all below.
      results[currentIndex] = await taskFn(items[currentIndex], currentIndex);
    }
  }

  // Spawn up to `concurrency` workers, capped by the number of items.
  const workerCount = Math.min(concurrency, items.length);
  const workers = Array.from({ length: workerCount }, () => worker());

  // Promise.all rejects as soon as any worker rejects, providing
  // the fail-fast behavior expected from this variant.
  await Promise.all(workers);

  return results;
}
